import { AxiosError } from 'axios';
import { Loader } from 'components/Loader/Loader';
import dotProp from 'dot-prop';
import useUserHavePermission from 'helpers/hooks/useUserHavePermission/useUserHavePermission';
import { getTasks } from 'modules/cm/Tasks/useCases';
import { CaseDetailsPage } from 'modules/cm/pages';
import { clearErrorModal, fetchCase, refreshCase } from 'modules/cm/store/cases/actions';
import { caseViewPermissionChange } from 'modules/cm/store/cases/permission/actions';
import { CaseErrorDetails, State } from 'modules/cm/store/cases/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { PAGES_ENUM, PERMISSION_ENUM } from 'routes/enums';
import { dashboard } from 'routes/routePaths';
import { PageComponentProps } from 'shared/interface/globalInterface';
import { resetContainerWidth, setContainerWidth } from 'store/layout/actions';
import { isNotFoundResponse } from 'utils/api/responseValidator';
import { init, setShowDialog } from 'utils/unsavedChangesDialog';
import { getIsCaseDirty } from '../../store/subPages/selectors';

type Props = PageComponentProps;


const CaseById: React.FC<Props> = ({ match, history }) => {
  const caseId = match.params.case_id;
  const dispatch = useDispatch();
  const caseState = useSelector(({ currentCase }: { currentCase: State }) => currentCase);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const havePermissionToWrite = useUserHavePermission(PAGES_ENUM.CASE, PERMISSION_ENUM.WRITE);
  const isDirty = useSelector(getIsCaseDirty);


  const getCaseErrorMessage = (error: AxiosError<CaseErrorDetails> | null) => {
    if (error && error.response && error.response.data.details) {
      return error.response.data.errorCode === 'COMPLEX' && error.response.data.details.detail
        ? error.response.data.details.detail.toString()
        : error.response.data.details.toString();
    }
    return null;
  };

  const dispatchRefreshCase = useCallback(async () => {
    setIsRefreshing(true);
    await dispatch(refreshCase(caseId));
    setIsRefreshing(false);
  }, [dispatch, caseId]);

  const dispatchFetchCase = useCallback(() => {
    dispatch(fetchCase(caseId));
  }, [dispatch, caseId]);

  useEffect(() => {
    dispatch(setContainerWidth('xl'));

    return () => {
      dispatch(resetContainerWidth());
      dispatch(clearErrorModal());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatchFetchCase();
  }, [dispatchFetchCase]);

  useEffect(() => {
    dispatch(getTasks(caseId));
  }, [dispatch, caseId]);

  useEffect(() => {
    if (caseState.error && isNotFoundResponse(caseState.error.response)) {
      history.replace(dashboard);
    }
  }, [caseState.error, history]);

  useEffect(() => {
    dispatch(caseViewPermissionChange(havePermissionToWrite));
  }, [dispatch, havePermissionToWrite]);

  useEffect(() => {
    init();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setShowDialog(isDirty);
    return () => setShowDialog(false);
  }, [isDirty]);

  const Component = useMemo(() => {
    // Hack to ensure correct rendered Case ID in the readonly field
    const roCaseId = dotProp.get(caseState.readonlyFields, 'caseId');

    return (
      caseState.data &&
      caseState.initialData &&
      (!roCaseId || roCaseId === caseId) &&
      caseState.isLoaded && (
        <CaseDetailsPage
          caseDetails={caseState.data}
          initialCaseDetails={caseState.initialData}
          url={match.url}
          onCaseRefresh={dispatchRefreshCase}
          onTabChange={() => { }}
          isDuringSave={false}
        />
      )
    );
  }, [
    caseState.readonlyFields,
    caseState.data,
    dispatchRefreshCase,
    caseId,
    caseState.initialData,
    caseState.isLoaded,
    match.url,
  ]);

  return (
    <>
      {(isRefreshing || caseState.data?.isSaving) && <Loader />}
      <Prompt
        when={isDirty}
        message={(location) => {
          return !location.pathname.includes(`cm/${caseId}`)
            ? 'Your changes have not been saved yet. Leave page anyway?'
            : true;
        }}
      />
      {caseState.error === null && Component}
      {caseState.error && (
        <>
          <p>Error occurred when loading a case</p>
          <p>{getCaseErrorMessage(caseState.error)}</p>
        </>
      )}
    </>
  );
};

export default CaseById;
